import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('davocado-cart');
    if (savedCart) {
      try {
        setItems(JSON.parse(savedCart));
      } catch (e) {
        console.error('Error loading cart:', e);
      }
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('davocado-cart', JSON.stringify(items));
  }, [items]);

  const addItem = (product, variant, quantity = 1) => {
    setItems(prev => {
      const existingIndex = prev.findIndex(
        item => item.productId === product.id && 
                item.size === variant.size && 
                item.color === variant.color
      );

      if (existingIndex > -1) {
        const updated = [...prev];
        updated[existingIndex].quantity += quantity;
        return updated;
      }

      return [...prev, {
        id: `${product.id}-${variant.size}-${variant.color}`,
        productId: product.id,
        name: product.name,
        price: product.price,
        size: variant.size,
        color: variant.color,
        quantity,
        image: product.images[0] || '',
        maxStock: variant.stock_quantity
      }];
    });
  };

  const removeItem = (itemId) => {
    setItems(prev => prev.filter(item => item.id !== itemId));
  };

  const updateQuantity = (itemId, quantity) => {
    if (quantity <= 0) {
      removeItem(itemId);
      return;
    }
    setItems(prev => prev.map(item => 
      item.id === itemId ? { ...item, quantity } : item
    ));
  };

  const clearCart = () => {
    setItems([]);
  };

  const openCart = () => setIsOpen(true);
  const closeCart = () => setIsOpen(false);
  const toggleCart = () => setIsOpen(prev => !prev);

  const subtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  const generateWhatsAppMessage = () => {
    if (items.length === 0) return '';

    let message = `🥑 *Nuevo Pedido - D'Avocado Swimwear*\n\n`;
    message += `¡Hola! Me gustaría ordenar los siguientes productos:\n\n`;
    message += `*Productos:*\n`;

    items.forEach(item => {
      message += `• ${item.name} - Talla: ${item.size} - Color: ${item.color} - Cantidad: ${item.quantity} - Precio: $${(item.price * item.quantity).toFixed(2)}\n`;
    });

    message += `\n*Subtotal:* $${subtotal.toFixed(2)}\n\n`;
    message += `Por favor confirmar disponibilidad y opciones de pago/envío. ¡Gracias! 🙏`;

    return encodeURIComponent(message);
  };

  const getWhatsAppUrl = () => {
    const message = generateWhatsAppMessage();
    return `https://wa.me/584242925792?text=${message}`;
  };

  return (
    <CartContext.Provider value={{
      items,
      isOpen,
      addItem,
      removeItem,
      updateQuantity,
      clearCart,
      openCart,
      closeCart,
      toggleCart,
      subtotal,
      itemCount,
      getWhatsAppUrl
    }}>
      {children}
    </CartContext.Provider>
  );
};
